<template>
  <div class="lead-page">

    <!-- ===== آمار ===== -->
    <b-row class="match-height stat-row">
      <b-col
        v-for="stat in statCards"
        :key="stat.key"
        cols="6"
        md="4"
        xl="2"
      >
        <b-card
          no-body
          class="stat-card"
          :class="{ 'is-active': activeStat === stat.key, 'is-clickable': !!stat.filter }"
          @click="applyStat(stat)"
        >
          <div class="stat-card__body">
            <b-avatar
              rounded
              size="42"
              :variant="`light-${stat.variant}`"
            >
              <feather-icon
                :icon="stat.icon"
                size="20"
              />
            </b-avatar>
            <div class="stat-card__text">
              <h4 class="mb-0">
                {{ stat.value }}
              </h4>
              <small class="text-muted">{{ stat.title }}</small>
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <!-- ===== فیلترها ===== -->
    <b-card no-body>
      <div class="p-2 pb-0">
        <div class="d-flex align-items-center justify-content-between flex-wrap mb-1">
          <h4 class="mb-0">
            مدیریت لیدها
          </h4>
          <div class="d-flex flex-wrap toolbar">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              :to="{ name: 'addlead' }"
            >
              <feather-icon
                icon="PlusIcon"
                class="ml-25"
              />
              ثبت لید جدید
            </b-button>
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              variant="outline-secondary"
              :disabled="exporting"
              @click="exportCsv"
            >
              <b-spinner
                v-if="exporting"
                small
                class="ml-25"
              />
              <feather-icon
                v-else
                icon="DownloadIcon"
                class="ml-25"
              />
              خروجی CSV
            </b-button>
          </div>
        </div>

        <b-row>
          <b-col
            md="3"
            sm="6"
          >
            <b-form-group
              label="جستجو (نام / موبایل / ایمیل / متن)"
              label-for="Search"
            >
              <b-input-group>
                <b-input-group-prepend is-text>
                  <feather-icon icon="SearchIcon" />
                </b-input-group-prepend>
                <b-form-input
                  id="Search"
                  v-model="filterOptions.Search"
                  placeholder="جستجو کنید…"
                  @keyup.enter="reload(1)"
                />
              </b-input-group>
            </b-form-group>
          </b-col>

          <b-col
            md="2"
            sm="6"
          >
            <b-form-group
              label="منبع"
              label-for="Source"
            >
              <v-select
                v-model="filterOptions.Source"
                :options="sourceOptions"
                dir="rtl"
                label="text"
                placeholder="همه"
                :reduce="x => x.value"
                @input="reload(1)"
              />
            </b-form-group>
          </b-col>

          <b-col
            md="2"
            sm="6"
          >
            <b-form-group
              label="کانال"
              label-for="Channel"
            >
              <v-select
                v-model="filterOptions.Channel"
                :options="channelOptions"
                dir="rtl"
                placeholder="همه"
                taggable
                @input="reload(1)"
              />
            </b-form-group>
          </b-col>

          <b-col
            md="2"
            sm="6"
          >
            <b-form-group
              label="اولویت"
              label-for="Priority"
            >
              <v-select
                v-model="filterOptions.Priority"
                :options="priorityOptions"
                dir="rtl"
                label="text"
                placeholder="همه"
                :reduce="x => x.value"
                @input="reload(1)"
              />
            </b-form-group>
          </b-col>

          <b-col
            md="3"
            sm="6"
          >
            <b-form-group
              label="کارشناس"
              label-for="AssignedUserId"
            >
              <div class="d-flex">
                <z-user
                  v-model="filterOptions.AssignedUserId"
                  class="flex-grow-1"
                  clearable
                  @input="reload(1)"
                />
                <b-button
                  v-b-tooltip.hover
                  size="sm"
                  class="mr-50 px-1"
                  title="بدون کارشناس"
                  :variant="filterOptions.AssignedUserId === 0 ? 'warning' : 'outline-secondary'"
                  @click="toggleUnassigned"
                >
                  <feather-icon icon="UserXIcon" />
                </b-button>
              </div>
            </b-form-group>
          </b-col>

          <b-col
            md="2"
            sm="6"
          >
            <b-form-group
              label="صف پیگیری"
              label-for="FollowUp"
            >
              <v-select
                v-model="filterOptions.FollowUp"
                :options="followUpOptions"
                dir="rtl"
                label="text"
                placeholder="همه"
                :reduce="x => x.value"
                @input="reload(1)"
              />
            </b-form-group>
          </b-col>

          <b-col
            md="2"
            sm="6"
          >
            <b-form-group
              label="از تاریخ"
              label-for="FromDate"
            >
              <date-picker
                v-model="filterOptions.FromDate"
                format="YYYY-MM-DD"
                display-format="jYYYY/jMM/jDD"
                :clearable="true"
                placeholder=""
                @change="reload(1)"
              />
            </b-form-group>
          </b-col>

          <b-col
            md="2"
            sm="6"
          >
            <b-form-group
              label="تا تاریخ"
              label-for="ToDate"
            >
              <date-picker
                v-model="filterOptions.ToDate"
                format="YYYY-MM-DD"
                display-format="jYYYY/jMM/jDD"
                :clearable="true"
                placeholder=""
                @change="reload(1)"
              />
            </b-form-group>
          </b-col>

          <b-col
            md="6"
            class="d-flex align-items-center mb-1"
          >
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              variant="warning"
              class="ml-1"
              @click="reload(1)"
            >
              اعمال فیلتر
            </b-button>
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              variant="flat-secondary"
              @click="resetFilter"
            >
              پاک کردن
            </b-button>
          </b-col>
        </b-row>
      </div>

      <!-- ===== تب‌ها ===== -->
      <b-tabs
        v-model="tabIndex"
        class="lead-tabs"
        nav-class="px-2"
        @input="onTabChange"
      >
        <!-- کانبان -->
        <b-tab>
          <template #title>
            <feather-icon
              icon="TrelloIcon"
              class="ml-25"
            />
            <span>کانبان</span>
          </template>

          <b-overlay
            :show="boardLoading"
            rounded="sm"
          >
            <div class="board">
              <div
                v-for="column in board"
                :key="column.Status"
                class="board__col"
              >
                <div
                  class="board__head"
                  :style="{ borderTopColor: statusColor(column.Status) }"
                >
                  <div>
                    <span class="board__title">{{ column.Title }}</span>
                    <b-badge
                      pill
                      class="mr-50"
                      :variant="statusVariant(column.Status)"
                    >
                      {{ column.TotalRow }}
                    </b-badge>
                  </div>
                  <small
                    v-if="column.EstimatedValue > 0"
                    class="text-muted"
                  >{{ column.EstimatedValue | price }} تومان</small>
                </div>

                <draggable
                  :list="column.Items"
                  :group="{ name: 'leads' }"
                  class="board__list"
                  ghost-class="board__card--ghost"
                  @change="onDrop($event, column)"
                >
                  <div
                    v-for="item in column.Items"
                    :key="item.Id"
                    class="board__card"
                    :class="{ 'is-overdue': isOverdue(item) }"
                    @click="openLead(item.Id)"
                  >
                    <div class="board__card-top">
                      <span
                        class="board__prio"
                        :style="{ background: priorityColor(item.Priority) }"
                      />
                      <strong class="board__name">{{ item.Name || 'بدون نام' }}</strong>
                      <b-badge
                        v-if="!item.IsRead"
                        variant="light-primary"
                        class="board__new"
                      >جدید</b-badge>
                    </div>

                    <div
                      v-if="item.Mobile"
                      class="board__mobile"
                      dir="ltr"
                    >
                      {{ item.Mobile }}
                    </div>

                    <p
                      v-if="item.Message"
                      class="board__msg"
                    >
                      {{ item.Message }}
                    </p>

                    <div class="board__meta">
                      <b-badge variant="light-secondary">
                        {{ sourceLabel(item.Source) }}
                      </b-badge>
                      <b-badge
                        v-if="item.Channel"
                        variant="light-info"
                      >
                        {{ item.Channel }}
                      </b-badge>
                      <b-badge
                        v-if="item.EstimatedValue"
                        variant="light-success"
                      >
                        {{ item.EstimatedValue | price }}
                      </b-badge>
                    </div>

                    <div class="board__foot">
                      <span
                        v-if="item.NextFollowUpAt"
                        class="board__follow"
                        :class="{ 'text-danger': isOverdue(item) }"
                      >
                        <feather-icon
                          icon="ClockIcon"
                          size="13"
                        />
                        {{ item.NextFollowUpAt | date }}
                      </span>
                      <span
                        v-else
                        class="text-muted small"
                      >{{ item.CreatedAt | date }}</span>

                      <b-avatar
                        v-if="item.AssignedUserName"
                        v-b-tooltip.hover
                        size="24"
                        variant="light-primary"
                        :title="item.AssignedUserName"
                        :text="avatarText(item.AssignedUserName)"
                      />
                      <b-avatar
                        v-else
                        v-b-tooltip.hover
                        size="24"
                        variant="light-secondary"
                        title="بدون کارشناس"
                      >
                        <feather-icon
                          icon="UserIcon"
                          size="13"
                        />
                      </b-avatar>
                    </div>
                  </div>

                  <div
                    v-if="!column.Items.length"
                    class="board__empty"
                  >
                    موردی نیست
                  </div>
                </draggable>

                <div
                  v-if="column.TotalRow > column.Items.length"
                  class="board__more"
                >
                  {{ column.TotalRow - column.Items.length }} مورد دیگر — از تب لیست ببینید
                </div>
              </div>
            </div>
          </b-overlay>
        </b-tab>

        <!-- لیست -->
        <b-tab>
          <template #title>
            <feather-icon
              icon="ListIcon"
              class="ml-25"
            />
            <span>لیست</span>
          </template>

          <div class="px-2">
            <div class="d-flex align-items-center mb-1 flex-wrap">
              <div class="ml-2">
                <label class="small text-muted d-block mb-25">وضعیت</label>
                <div class="status-chips">
                  <span
                    class="chip"
                    :class="{ 'chip--on': filterOptions.Status === null }"
                    @click="setStatusFilter(null)"
                  >همه</span>
                  <span
                    v-for="s in statusOptions"
                    :key="s.value"
                    class="chip"
                    :class="[{ 'chip--on': filterOptions.Status === s.value }]"
                    :style="filterOptions.Status === s.value ? { background: s.color, borderColor: s.color, color: '#fff' } : {}"
                    @click="setStatusFilter(s.value)"
                  >{{ s.text }}</span>
                </div>
              </div>
              <div>
                <label class="small text-muted d-block mb-25">مرتب‌سازی</label>
                <v-select
                  v-model="filterOptions.SortBy"
                  :options="sortOptions"
                  dir="rtl"
                  label="text"
                  style="min-width: 190px"
                  :clearable="false"
                  :reduce="x => x.value"
                  @input="reload(1)"
                />
              </div>
            </div>

            <b-table
              bordered
              hover
              striped
              responsive
              class="mt-1 mb-2 position-relative lead-table"
              :items="items"
              :fields="fields"
              :busy="loading"
              :tbody-tr-class="rowClass"
            >
              <template #cell(Id)="data">
                <b-link
                  class="font-weight-bold"
                  :to="{ name: 'updatelead', params: { id: data.value } }"
                >#{{ data.value }}</b-link>
              </template>

              <template #cell(Name)="data">
                <div class="d-flex align-items-center">
                  <span
                    class="prio-dot ml-50"
                    :style="{ background: priorityColor(data.item.Priority) }"
                    :title="priorityLabel(data.item.Priority)"
                  />
                  <div>
                    <b-link
                      class="font-weight-bolder d-block"
                      :to="{ name: 'updatelead', params: { id: data.item.Id } }"
                    >{{ data.value || 'بدون نام' }}</b-link>
                    <small
                      v-if="data.item.Mobile"
                      class="text-muted"
                      dir="ltr"
                    >{{ data.item.Mobile }}</small>
                  </div>
                </div>
              </template>

              <template #cell(Source)="data">
                <b-badge variant="light-secondary">
                  {{ sourceLabel(data.value) }}
                </b-badge>
                <div
                  v-if="data.item.Channel"
                  class="mt-25"
                >
                  <b-badge variant="light-info">
                    {{ data.item.Channel }}
                  </b-badge>
                </div>
              </template>

              <template #cell(Message)="data">
                <span
                  v-b-tooltip.hover
                  class="text-truncate d-inline-block"
                  style="max-width: 220px"
                  :title="data.value"
                >{{ data.value }}</span>
              </template>

              <template #cell(AssignedUserName)="data">
                <span v-if="data.value">{{ data.value }}</span>
                <span
                  v-else
                  class="text-muted"
                >—</span>
              </template>

              <template #cell(EstimatedValue)="data">
                <span v-if="data.value">{{ data.value | price }}</span>
                <span
                  v-else
                  class="text-muted"
                >—</span>
              </template>

              <template #cell(NextFollowUpAt)="data">
                <span
                  v-if="data.value"
                  :class="{ 'text-danger font-weight-bold': isOverdue(data.item) }"
                >{{ data.value | date }}</span>
                <span
                  v-else
                  class="text-muted"
                >—</span>
              </template>

              <template #cell(CreatedAt)="data">
                {{ data.value | date }}
              </template>

              <template #cell(Status)="data">
                <v-select
                  :value="statusOf(data.item.Status)"
                  :options="statusOptions"
                  dir="rtl"
                  label="text"
                  style="min-width: 145px"
                  :clearable="false"
                  :searchable="false"
                  @input="quickStatus(data.item, $event)"
                />
              </template>

              <template #cell(actions)="data">
                <div class="d-flex align-items-center action-cell">
                  <b-link
                    v-b-tooltip.hover
                    title="جزئیات و پیگیری"
                    :to="{ name: 'updatelead', params: { id: data.item.Id } }"
                  >
                    <feather-icon
                      icon="EyeIcon"
                      size="16"
                    />
                  </b-link>
                  <a
                    v-if="data.item.Mobile"
                    v-b-tooltip.hover
                    title="تماس"
                    :href="'tel:' + data.item.Mobile"
                  >
                    <feather-icon
                      icon="PhoneIcon"
                      size="16"
                    />
                  </a>
                  <a
                    v-if="waLink(data.item.Mobile)"
                    v-b-tooltip.hover
                    title="واتساپ"
                    target="_blank"
                    :href="waLink(data.item.Mobile)"
                  >
                    <feather-icon
                      icon="MessageCircleIcon"
                      size="16"
                    />
                  </a>
                  <feather-icon
                    v-b-tooltip.hover
                    icon="TrashIcon"
                    size="16"
                    class="cursor-pointer text-danger"
                    title="حذف"
                    @click="remove(data.item.Id)"
                  />
                </div>
              </template>

              <template #table-busy>
                <div class="text-center my-2">
                  <b-spinner
                    variant="primary"
                    class="align-middle"
                  />
                  <strong class="ml-1">در حال دریافت اطلاعات ...</strong>
                </div>
              </template>

              <template #empty>
                <div class="text-center text-muted my-2">
                  لیدی با این فیلترها پیدا نشد
                </div>
              </template>
            </b-table>

            <z-pagination
              :page="filterOptions.Page"
              :per-page="filterOptions.PerPage"
              :total-rows="totalRows"
              :loading="loading"
              @change="update"
            />
          </div>
        </b-tab>
      </b-tabs>
    </b-card>
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import ZUser from '@/tools/user.vue'
import {
  STATUS, PRIORITY, SOURCE, CHANNEL, FOLLOW_UP,
  statusOf, priorityOf, sourceOf, waLink,
} from './leadOptions'

const emptyFilter = () => ({
  Search: null,
  Status: null,
  Source: null,
  Channel: null,
  Priority: null,
  AssignedUserId: null,
  FollowUp: null,
  FromDate: null,
  ToDate: null,
  SortBy: 'Id',
  IsDesc: true,
  Page: 1,
  PerPage: 20,
})

export default {
  name: 'Lead',
  components: { draggable, ZUser },
  data() {
    return {
      tabIndex: 0,
      loading: false,
      boardLoading: false,
      exporting: false,
      totalRows: 0,
      items: [],
      board: [],
      summary: {},
      activeStat: null,
      filterOptions: emptyFilter(),
      statusOptions: STATUS,
      priorityOptions: PRIORITY,
      sourceOptions: SOURCE,
      channelOptions: CHANNEL,
      followUpOptions: FOLLOW_UP,
      sortOptions: [
        { value: 'Id', text: 'جدیدترین' },
        { value: 'NextFollowUpAt', text: 'نزدیک‌ترین پیگیری' },
        { value: 'Priority', text: 'بیشترین اولویت' },
        { value: 'LastActivityAt', text: 'آخرین فعالیت' },
      ],
      fields: [
        { key: 'Id', label: '#', thClass: 'col-width-50' },
        { key: 'Name', label: 'لید' },
        { key: 'Source', label: 'منبع' },
        { key: 'Message', label: 'پیام' },
        { key: 'AssignedUserName', label: 'کارشناس' },
        { key: 'EstimatedValue', label: 'مبلغ تخمینی' },
        { key: 'NextFollowUpAt', label: 'پیگیری بعدی' },
        { key: 'CreatedAt', label: 'تاریخ ثبت' },
        { key: 'Status', label: 'وضعیت' },
        { key: 'actions', label: '', thClass: 'col-width-50' },
      ],
    }
  },
  computed: {
    statCards() {
      const s = this.summary
      return [
        {
          key: 'today',
          title: 'لید امروز',
          value: s.NewToday || 0,
          icon: 'InboxIcon',
          variant: 'primary',
        },
        {
          key: 'untouched',
          title: 'پیگیری‌نشده',
          value: s.Untouched || 0,
          icon: 'AlertCircleIcon',
          variant: 'warning',
          filter: { Status: 0 },
        },
        {
          key: 'overdue',
          title: 'پیگیری عقب‌افتاده',
          value: s.Overdue || 0,
          icon: 'ClockIcon',
          variant: 'danger',
          filter: { FollowUp: 0 },
        },
        {
          key: 'followUpToday',
          title: 'پیگیری امروز',
          value: s.FollowUpToday || 0,
          icon: 'CalendarIcon',
          variant: 'info',
          filter: { FollowUp: 1 },
        },
        {
          key: 'won',
          title: 'موفق (۳۰ روز)',
          value: s.WonMonth || 0,
          icon: 'CheckCircleIcon',
          variant: 'success',
          filter: { Status: 2 },
        },
        {
          key: 'rate',
          title: 'نرخ تبدیل',
          value: `${s.ConversionRate || 0}٪`,
          icon: 'TrendingUpIcon',
          variant: 'secondary',
        },
      ]
    },
    // فیلترهای مشترک لیست و کانبان (بدون صفحه‌بندی و وضعیت)
    sharedFilter() {
      const f = this.filterOptions
      return {
        Search: f.Search,
        Source: f.Source,
        Channel: f.Channel,
        Priority: f.Priority,
        AssignedUserId: f.AssignedUserId,
        FollowUp: f.FollowUp,
        FromDate: f.FromDate,
        ToDate: f.ToDate,
      }
    },
  },
  created() {
    const saved = this.$store.state.zirehState.listFilter[this.$options.name]
    if (saved) {
      this.filterOptions = { ...emptyFilter(), ...saved }
    }
    this.reload()
  },
  methods: {
    statusOf,
    priorityOf,
    waLink,
    sourceLabel(v) {
      return sourceOf(v).text
    },
    statusVariant(v) {
      return `light-${statusOf(v).variant}`
    },
    statusColor(v) {
      return statusOf(v).color
    },
    priorityColor(v) {
      return { 0: '#b9b9c3', 1: '#ff9f43', 2: '#ea5455' }[v] || '#b9b9c3'
    },
    priorityLabel(v) {
      return priorityOf(v).text
    },
    isOverdue(item) {
      if (!item.NextFollowUpAt) return false
      if (item.Status === 2 || item.Status === 3) return false
      return this.$moment(item.NextFollowUpAt).isBefore(this.$moment())
    },
    rowClass(item) {
      if (!item) return ''
      if (!item.IsRead) return 'row-unread'
      return ''
    },

    reload(pageNumber) {
      this.$store.commit('zirehState/setListFilter', { key: this.$options.name, value: this.filterOptions })
      this.getSummary()
      if (this.tabIndex === 0) {
        this.getBoard()
      } else {
        this.update(pageNumber)
      }
    },
    onTabChange() {
      // تب مقابل ممکن است داده‌ی قدیمی داشته باشد
      this.$nextTick(() => this.reload())
    },
    update(pageNumber) {
      this.loading = true
      this.filterOptions.Page = pageNumber || this.filterOptions.Page || 1

      this.$http.post(this.$api.lead.getlist, { ...this.sharedFilter, ...this.filterOptions })
        .then(res => {
          this.totalRows = res.totalRow
          this.items = res.data
        })
        .finally(() => {
          this.loading = false
        })
    },
    getBoard() {
      this.boardLoading = true
      this.$http.post(this.$api.lead.board, { ...this.sharedFilter, Take: 50 })
        .then(res => {
          this.board = res.data
        })
        .finally(() => {
          this.boardLoading = false
        })
    },
    getSummary() {
      this.$http.post(this.$api.lead.summary, { AssignedUserId: this.filterOptions.AssignedUserId })
        .then(res => {
          this.summary = res.data || {}
        })
    },

    // ===== فیلترها =====
    applyStat(stat) {
      if (!stat.filter) return

      if (this.activeStat === stat.key) {
        this.activeStat = null
        this.filterOptions = { ...emptyFilter(), Search: this.filterOptions.Search }
      } else {
        this.activeStat = stat.key
        this.filterOptions = { ...emptyFilter(), Search: this.filterOptions.Search, ...stat.filter }
      }

      this.reload(1)
    },
    setStatusFilter(value) {
      this.filterOptions.Status = value
      this.reload(1)
    },
    toggleUnassigned() {
      this.filterOptions.AssignedUserId = this.filterOptions.AssignedUserId === 0 ? null : 0
      this.reload(1)
    },
    resetFilter() {
      this.activeStat = null
      this.filterOptions = emptyFilter()
      this.reload(1)
    },

    // ===== عملیات =====
    openLead(id) {
      this.$router.push({ name: 'updatelead', params: { id } })
    },
    quickStatus(item, option) {
      if (!option || option.value === item.Status) return

      this.$http.post(this.$api.lead.setstatus, { Id: item.Id, Status: option.value })
        .then(() => {
          item.Status = option.value
          item.IsRead = true
          this.getSummary()
        })
    },
    onDrop(event, column) {
      if (!event.added) return

      const item = event.added.element
      const previous = item.Status

      this.$http.post(this.$api.lead.setstatus, { Id: item.Id, Status: column.Status })
        .then(() => {
          item.Status = column.Status
          item.IsRead = true
          if (column.Status === 2 || column.Status === 3) {
            item.NextFollowUpAt = null
          }
          this.getSummary()
          this.refreshCounts(previous, column.Status)
        })
        .catch(() => {
          // برگرداندن کارت سر جای اول در صورت خطا
          this.getBoard()
        })
    },
    refreshCounts(fromStatus, toStatus) {
      const from = this.board.find(x => x.Status === fromStatus)
      const to = this.board.find(x => x.Status === toStatus)
      if (from) from.TotalRow = Math.max(0, from.TotalRow - 1)
      if (to) to.TotalRow += 1
    },
    remove(id) {
      this.removeRow(this.$api.lead.remove, 'Id', id)
    },
    exportCsv() {
      this.exporting = true
      this.$http.post(this.$api.lead.export, { ...this.sharedFilter, Take: 5000 })
        .then(res => {
          const payload = res.data
          if (!payload || !payload.TotalRow) {
            this.$bvToast.toast('موردی برای خروجی گرفتن پیدا نشد', { title: 'خروجی CSV', variant: 'warning', solid: false })
            return
          }
          // BOM لازم است تا اکسل متن فارسی را درست بخواند
          const blob = new Blob([`\uFEFF${payload.Content}`], { type: 'text/csv;charset=utf-8;' })
          const link = document.createElement('a')
          link.href = URL.createObjectURL(blob)
          link.download = payload.FileName
          link.click()
          URL.revokeObjectURL(link.href)
        })
        .finally(() => {
          this.exporting = false
        })
    },
  },
}
</script>

<style lang="scss" scoped>
.stat-row {
  margin-bottom: 0;
}

.stat-card {
  margin-bottom: 1.5rem;
  transition: box-shadow .18s ease, transform .18s ease;

  &.is-clickable {
    cursor: pointer;
  }

  &.is-clickable:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 18px rgba(34, 41, 47, .12);
  }

  &.is-active {
    box-shadow: 0 0 0 2px var(--primary, #7367f0) inset;
  }

  &__body {
    display: flex;
    align-items: center;
    gap: .75rem;
    padding: 1rem 1.25rem;
  }

  &__text h4 {
    line-height: 1.2;
  }
}

.toolbar {
  gap: .5rem;
}

.lead-tabs ::v-deep .nav-tabs {
  margin-bottom: 1rem;
}

/* ===== کانبان ===== */
.board {
  display: flex;
  gap: 1rem;
  overflow-x: auto;
  padding: 0 2rem 1.5rem;
  align-items: flex-start;
}

.board__col {
  flex: 0 0 265px;
  max-width: 265px;
  background: rgba(186, 191, 199, .12);
  border-radius: .5rem;
  padding: .6rem;
}

.board__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: .25rem;
  padding: .5rem .5rem .6rem;
  border-top: 3px solid transparent;
  margin: -.6rem -.6rem .5rem;
  border-radius: .5rem .5rem 0 0;
}

.board__title {
  font-weight: 600;
}

.board__list {
  min-height: 90px;
  max-height: 62vh;
  overflow-y: auto;
  padding-left: 2px;
}

.board__card {
  background: #fff;
  border-radius: .428rem;
  padding: .65rem .7rem;
  margin-bottom: .6rem;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(34, 41, 47, .08);
  transition: box-shadow .15s ease, transform .15s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 14px rgba(34, 41, 47, .16);
  }

  &.is-overdue {
    border-right: 3px solid #ea5455;
  }

  &--ghost {
    opacity: .45;
  }
}

.board__card-top {
  display: flex;
  align-items: center;
  gap: .4rem;
  margin-bottom: .3rem;
}

.board__prio {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex: 0 0 auto;
}

.board__name {
  font-size: .95rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.board__new {
  font-size: .65rem;
  margin-right: auto;
}

.board__mobile {
  font-size: .8rem;
  color: #6e6b7b;
  margin-bottom: .3rem;
}

.board__msg {
  font-size: .78rem;
  color: #6e6b7b;
  margin-bottom: .4rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.board__meta {
  display: flex;
  flex-wrap: wrap;
  gap: .25rem;
  margin-bottom: .45rem;

  .badge {
    font-size: .65rem;
  }
}

.board__foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid rgba(186, 191, 199, .3);
  padding-top: .4rem;
}

.board__follow {
  font-size: .75rem;
  color: #6e6b7b;
}

.board__empty {
  text-align: center;
  color: #b9b9c3;
  font-size: .8rem;
  padding: 1.2rem 0;
}

.board__more {
  text-align: center;
  font-size: .72rem;
  color: #6e6b7b;
  padding-top: .3rem;
}

/* ===== لیست ===== */
.status-chips {
  display: flex;
  flex-wrap: wrap;
  gap: .35rem;
}

.chip {
  border: 1px solid rgba(186, 191, 199, .6);
  border-radius: 1rem;
  padding: .25rem .75rem;
  font-size: .8rem;
  cursor: pointer;
  user-select: none;
  transition: all .15s ease;

  &:hover {
    border-color: #7367f0;
  }

  &--on {
    background: #7367f0;
    border-color: #7367f0;
    color: #fff;
  }
}

.prio-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
  flex: 0 0 auto;
}

.action-cell {
  gap: .6rem;
}

.lead-table ::v-deep tr.row-unread td:first-child {
  box-shadow: inset 3px 0 0 #7367f0;
}

/* ===== حالت تیره ===== */
.dark-layout {
  .board__col {
    background: rgba(22, 29, 49, .5);
  }

  .board__card {
    background: #283046;
    box-shadow: 0 2px 6px rgba(0, 0, 0, .25);

    &:hover {
      box-shadow: 0 4px 14px rgba(0, 0, 0, .4);
    }
  }

  .board__mobile,
  .board__msg,
  .board__follow,
  .board__more {
    color: #b4b7bd;
  }

  .chip {
    border-color: rgba(180, 183, 189, .35);
  }

  .stat-card.is-clickable:hover {
    box-shadow: 0 6px 18px rgba(0, 0, 0, .4);
  }
}
</style>
